// Central content store — edit copy here without touching component code.

export const identity = {
  name: 'Shubh Joshi',
  role: 'Product · AI · Engineering',
  location: 'Toronto, Canada',
  email: 'shubh.joshi@gmail.com',
  phone: '647-988-5379',
  resumeUrl: '/Development.pdf',
  social: {
    linkedin: 'https://www.linkedin.com/in/shubhjoshi/',
    github: 'https://github.com/shubh186',
    instagram: 'https://instagram.com/shubhjoshi_',
    twitter: 'https://twitter.com/shubh55435927',
  },
};

export const heroCopy = {
  eyebrow: 'Toronto — Product, AI & Engineering',
  lines: ['I build things.', 'I align the room.', 'Usually at the same time.'],
  sub:
    "I'm a product manager who still ships code — right now leading global initiatives into production and scaling them, while working with the most current and adaptive technology available.",
  ctaPrimary: { label: 'See what I’ve built', href: '#projects' },
  ctaSecondary: { label: "Let's talk", href: '#contact' },
  available: 'Open to new projects',
};

export const toolkit = [
  'Product Strategy & Roadmapping', 'Agile/Scrum Leadership', 'Cross-Functional Team Management',
  'AI/LLM Product Development', 'Technical Requirements & API Design', 'Stakeholder Management',
  'Data-Driven Decision Making', 'Backlog Prioritization', 'System Architecture Collaboration',
  'Python', 'C/C++', 'JavaScript', 'Java', 'PowerShell',
  'MySQL/MSSQL', 'MongoDB', 'React.js', 'Next.js', 'Node.js', 'AKS', 'AWS', 'Flutter', 'Selenium', 'JMeter',
];

export const manifesto = {
  eyebrow: '01 — A little about me',
  heading: "I'm happiest in a room where people are figuring something out together.",
  paragraphs: [
    "I could describe myself as a product manager, or a developer, or a computer science grad — all true, all incomplete. What actually drives me is the moment a vague idea starts turning into something real, and the pull I feel toward being in that room.",
    "I like people. I like the chaos of a whiteboard session with too many opinions in it. I like translating between the engineer who wants to do it right and the exec who wants it by Friday. Most of what I've built professionally has happened at that intersection — not despite the people problems, but because of them.",
    "And I'm genuinely curious about where AI actually creates value versus where it's just a good demo. I'd rather prototype something ugly and learn from it than debate it in a slide deck.",
  ],
  portrait: '/images/me.jpg',
  quote: '“Give me a hard problem, a whiteboard, and a few skeptical engineers — that’s a good day.”',
};

export const pillars = [
  {
    id: 'people',
    tag: 'People',
    title: 'I bring the room together.',
    body:
      "I've led cross-functional teams of engineers, designers, Quality Assurance engineers, and stakeholders who didn't always agree on the problem, let alone the solution. My job is to get everyone building the same thing — and to make it a good time while we do it.",
    skills: [],
    achievement: 'Led cross-functional teams of engineers, designers, Quality Assurance engineers, and stakeholders to deliver global systems, end to end.',
  },
  {
    id: 'product',
    tag: 'Product',
    title: 'I turn ambiguity into a roadmap.',
    body:
      'I take a fuzzy problem, talk to the people living it, and come back with a plan the team can actually execute against — then stay close enough to engineering to know when the plan needs to change.',
    skills: [
      'Product Strategy & Roadmapping', 'Agile/Scrum Leadership', 'Cross-Functional Team Management',
      'AI/LLM Product Development', 'Technical Requirements & API Design', 'Stakeholder Management',
      'Data-Driven Decision Making', 'Backlog Prioritization', 'System Architecture Collaboration',
    ],
    achievement:
      'Took a global LMS from architecture into production, including an AI content-generation model that served 20,000+ questions and saved 4,000+ hours of instructor creation time.',
  },
  {
    id: 'technology',
    tag: 'Technology',
    title: "I can build what I design.",
    body:
      "I don't hand off a spec and disappear. I've shipped production APIs, data models, and full-stack features myself, which means I design things that are actually buildable — and I know when engineering is telling me the truth about timelines.",
    skills: [
      'Python', 'C/C++', 'JavaScript', 'Java', 'PowerShell', 'Node.js', 'Next.js', 'React.js',
      'MySQL/MSSQL', 'MongoDB', 'Redis', 'Kubernetes', 'Socket.IO', 'AKS', 'AWS', 'Flutter', 'WordPress',
    ],
    achievement: null,
  },
  {
    id: 'ai',
    tag: 'AI & Innovation',
    title: 'I look for what AI can make more efficient.',
    body:
      "I'm always looking for friction — a slow process, a manual task, a gap between data and a decision — that AI can actually make more efficient. I'd rather build a rough version and see where it breaks than debate it in theory.",
    skills: [],
    achievement:
      "Currently building a Chrome extension that flags real vs. passive content viewing, and an AI-assisted job-hunt and interview tracker — both early, both teaching me something new.",
  },
  {
    id: 'testing',
    tag: 'Testing & Automation',
    title: "I don't ship on faith.",
    body:
      "Shipping fast doesn't mean shipping blind. I automated tests through Playwright — unit, functional, regression, and end-to-end — alongside Selenium for browser automation and Apache JMeter for load and performance testing, all wired into CI/CD at a production level.",
    skills: ['Playwright', 'Selenium', 'Apache JMeter'],
    achievement:
      'Automated unit, functional, regression, end-to-end, and load/performance testing, built and run at a production level within CI/CD pipelines.',
  },
  {
    id: 'design',
    tag: 'Design',
    title: "I care how it works and how it looks.",
    body:
      "I'm closely involved on the design side of everything I build — sitting in on design reviews, sketching mockups myself, and working within our design system in production rather than handing sketches off and hoping they survive implementation.",
    skills: ['Figma', 'Design Reviews', 'Design Systems'],
    achievement: 'Sit in on design reviews and work directly within our production design system — not just the engineering handoff.',
  },
];

export const journey = [
  {
    id: 'prepdoctors',
    period: '01/2024 — Present',
    org: 'Prep Doctors Corp',
    role: 'Tech Lead & Product Manager',
    location: 'Mississauga, ON',
    impact: 'Own strategy for every tech project at Prep Doctors — serving as tech lead and product manager across all products and teams, globally.',
    points: [
      'Led product strategy and execution for a 10-microservice AI-driven Medical LMS, architecting a platform that scaled to 10,000+ concurrent users using Next.js, Node.js, MSSQL, Redis, Kubernetes, and Socket.IO.',
      'Built and fine-tuned an LLM-based question generation pipeline (GPT-3 Mini via Ollama, Google APIs), producing 20,000+ board-certified dental exam questions and cutting instructor workload by 4,000+ hours.',
      'Defined requirements and delivered RESTful APIs integrating CRM platforms (Zoho, HubSpot), plus internal APIs supporting custom requests and core system functionality.',
      'Delivered full-stack web applications on WordPress, React.js, Node.js, MySQL, InMotion, and AKS, partnering with engineering from requirements through release.',
      'Designed system architecture and relational data models with cross-functional product teams, aligning technical structure to business requirements.',
    ],
  },
  {
    id: 'ucmas',
    period: '11/2023 — Present',
    org: 'UCMAS',
    tag: 'Community',
    tagVariant: 'success',
    role: 'Mental Math Instructor',
    location: 'Mississauga, ON',
    impact:
      "Run my own weekly mental-math classes for elementary and middle-school students — a different kind of product, watching a kid's confidence compound instead of a metric.",
    points: [
      'Teach abacus-based mental math to elementary and middle-school students across structured weekly classes.',
      'Plan and run my own curriculum pacing, adapting to each student\'s level rather than following a single fixed track.',
    ],
  },
  {
    id: 'sunlife',
    period: '01/2022 — 08/2022',
    org: 'Sun Life Financial',
    role: 'Software Developer',
    location: 'Waterloo, ON',
    impact: 'Migrated critical file-transfer infrastructure from FTP to SFTP without disrupting live processes.',
    points: [
      'Developed server-side components using PowerShell to securely migrate file transfers from FTP to SFTP.',
      'Implemented XML-based storage and querying in MSSQL using XML data types and XQuery, for structured data manipulation and tracking.',
      'Developed SQL and PowerShell scripts for streamlined data extraction and analysis within SQL databases.',
    ],
  },
  {
    id: 'cibc',
    period: '05/2020 — 12/2020',
    org: 'CIBC',
    role: 'Application Developer',
    location: 'Toronto, ON',
    impact: 'Helped move a legacy SharePoint estate into the cloud without losing a decade of institutional data.',
    points: [
      'Supported the migration from SharePoint 2013 to SharePoint Online by optimizing PowerShell-based ETL scripts to extract and stage legacy data in MSSQL pre deployment.',
      'Created Power Automate Flows to automate repetitive business processes, improving operational efficiency by 20%.',
    ],
  },
  {
    id: 'education',
    period: '2018 — 2023',
    org: 'University of Guelph',
    role: 'B.Comp., Computer Science',
    location: 'Guelph, ON',
    impact: 'Where the product-and-engineering combination started — coursework plus every side project I could squeeze in.',
    points: [],
  },
];

// Deep case studies — the flagship work, told as a story rather than a spec sheet.
export const caseStudies = [
  {
    id: 'prepx',
    number: '01',
    name: 'AI Medical Learning Management System',
    tagline: 'The platform pharmacy, nursing, and dental candidates use to prep for board certification — globally',
    highlight: '10,000+ concurrent users · 45% efficiency gain in year one',
    status: 'Production',
    role: 'Product Lead & Engineering',
    tags: [
      'Product Strategy', 'Stakeholder Management', 'System Architecture', 'Microservices', 'Full-Stack Development',
      'Data Modeling', 'API Development', 'WebSockets', 'Redis', 'Design', 'Automated Testing', 'Azure Kubernetes', 'HubSpot', 'AWS',
    ],
    problem:
      'This platform serves pharmacy, nursing, and dental professionals preparing for board certification exams around the world — and it was buckling under real usage: timer drift and socket load issues during live exams, plus a structure that didn\'t reflect how the business actually delivers programs across both physical city campuses and a fast-growing online cohort.',
    idea:
      'Re-architect the platform around how the business actually operates — treating "Online" as a first-class campus alongside physical locations, fixing the timing and load issues at the infrastructure level, and building toward a global-scale architecture as the business expands into new markets.',
    approach:
      'Own the architecture end to end on Azure Kubernetes — re-architecting to fix timer drift and socket load issues, then designing the enrollment sync from HubSpot and the course management, calendaring, content-release, and announcement systems learners depend on. Now leading a new AWS-based architecture to support global expansion beyond the current footprint.',
    built:
      'A 10-microservice platform on Azure Kubernetes (AKS) serving pharmacy, nursing, and dental learners, with "Online" modeled as a first-class campus alongside physical city campuses. Covers course management, calendaring, content-release rules, and announcements, fed by a real-time enrollment sync from HubSpot.',
    challenge:
      'Fixing timer drift and socket load issues in a live system serving 10,000+ concurrent users during real exam windows — the kind of infrastructure problem that only shows up under real load, never in a demo.',
    impact: 'A platform that holds up under 10,000+ concurrent learners across pharmacy, nursing, and dental programs, improving business efficiency by over 45% in year one — now expanding onto a new AWS-based architecture as the business scales globally.',
    learned:
      'The most valuable architecture work is often invisible — nobody notices timer drift and socket load issues until you fix them. Re-architecting for problems you can actually measure taught me more about scale than any greenfield build would have.',
  },
  {
    id: 'aicontentengine',
    number: '02',
    name: 'AI Medical Content Generation Model',
    tagline: 'Led a global initiative meeting directly with pharmacy, nursing, and dental professionals to understand where the real pain was in writing exam content',
    highlight: '20,000+ questions generated · 4,000+ hours saved',
    status: 'Production',
    role: 'Product Lead & Engineering',
    tags: ['LLMs', 'Prompt Engineering', 'Ollama', 'Google APIs', 'Team Leadership', 'Stakeholder Research'],
    problem:
      'Writing board-exam-quality practice questions by hand doesn’t scale — instructors across pharmacy, nursing, and dental programs were spending hours per question, and the student base was growing faster than the question bank.',
    idea:
      'Use an LLM to draft candidate questions in the instructors’ own style and difficulty calibration, then let instructors edit and approve rather than write from scratch.',
    approach:
      'I led this initiative end to end — meeting with instructors globally to understand where the pain actually was, then leading a team of two engineers through iterative development focused on prompt engineering and fast feedback loops, defining what "board-realistic" meant together with the instructors along the way.',
    built:
      'An LLM-based question-generation pipeline (GPT-3 Mini via Ollama, Google APIs) that produces board-certified-style exam questions, with an instructor review layer built in so nothing ships unverified.',
    challenge:
      'The technical part was the easy half. The real challenge was earning instructor trust — getting subject-matter experts comfortable treating an LLM’s output as a first draft worth editing, not something to distrust by default.',
    impact: '20,000+ board-certified questions generated, cutting instructor workload by 4,000+ hours — since expanded to generate full clinical case studies as well.',
    learned:
      'For applied AI, the adoption problem is usually a trust problem, not a capability problem. Framing the model as a co-writer instructors could edit — not a black box — is what actually got it used.',
  },
  {
    id: 'jainbites',
    number: '03',
    name: 'Jain Bites',
    tagline: 'Finding a restaurant that fits a strict diet shouldn’t take 20 phone calls',
    highlight: '3,000+ downloads across iOS & Android',
    status: 'Production',
    role: 'Engineering',
    tags: ['JavaScript', 'Apache Cordova', 'Google APIs', 'HTML/CSS'],
    appStoreUrl: 'https://apps.apple.com/ca/app/jain-bites/id1471277875',
    problem:
      'People following Jain dietary restrictions (no root vegetables, strict vegetarian) across the Greater Toronto Area had no reliable way to find restaurants that could actually accommodate them.',
    idea:
      'A crowdsourced, mobile-first directory that let the community itself flag and verify Jain-friendly spots, instead of relying on a small team to manually vet every restaurant.',
    approach:
      'Designed and built the app solo, end to end — from the data model for restaurant verification to the mobile release process for both app stores.',
    built:
      'A cross-platform mobile app using JavaScript, Apache Cordova, Firebase, and the Google Maps and Sheets APIs, shipped to both the App Store and Google Play.',
    challenge:
      'Keeping restaurant data trustworthy without a moderation team — the answer was leaning on the community to self-report and cross-verify listings.',
    impact: 'Grew to 3,000+ users across iOS and Android with no marketing budget, purely word of mouth within the community.',
    learned:
      'A niche, deeply-felt problem beats a broad, shallow one. This app will never be huge, but the people who use it really need it — and that’s a different, quieter kind of product success.',
  },
  {
    id: 'chromeext',
    number: '04',
    name: 'PixelTruth — Real-Time Media Authenticity & Provenance Extension',
    tagline: 'A browser extension that labels every image and video on your screen as Authentic, Edited, or AI-generated — in real time',
    highlight: 'Real-time detection, live in the browser',
    status: 'Seeding Users',
    role: 'Product & Engineering',
    tags: [
      'Manifest V3', 'TypeScript & React', 'Python & FastAPI', 'CLIP & XceptionNet',
      'PRNU/NoisePrint', 'Redis', 'Docker & GitHub Actions', 'Roadmapping',
    ],
    problem:
      'AI-generated and edited media is everywhere now, and there\'s no way to tell what\'s real while you\'re actually scrolling — you\'d have to stop and run it through a separate detector.',
    idea:
      'A browser extension that passively watches every image and video as it loads and labels it Authentic, Edited, or AI-generated in real time, with zero user input.',
    approach:
      'Owned this end to end as a solo product — architecture, data model, and the detection pipeline — the same way I\'d scope a real platform. Wrote the roadmap and backlog myself and prioritized what shipped first.',
    built:
      'A Manifest V3 Chrome extension (TypeScript + React) that uses DOM observation to catch images and video as they load, sends them to a FastAPI backend, and overlays a floating authenticity label beside each one. The detection layer fuses multiple models — PRNU/NoisePrint for camera-sensor fingerprinting, ManTra-Net for local manipulation detection, and XceptionNet/CLIP-based models for GAN and diffusion artifacts — into a single confidence-weighted classification. Video adds frame sampling plus CNN-LSTM/F3-Net temporal-consistency checks to catch deepfakes frame to frame. Redis caches results by hashed media URL so nothing gets re-analyzed twice, running on Docker with GitHub Actions CI/CD.',
    challenge:
      'Making real-time detection actually feel real-time — analyzing full-resolution media on every scroll would kill browser performance, so the system hashes and caches repeat media, throttles analysis per scroll, and works off thumbnails instead of full images wherever it can.',
    impact:
      'Development is complete. Now in a growth phase, focused on marketing to build out its first real user base.',
    learned:
      'Running my own roadmap and backlog for a personal project made it obvious how much of product management is prioritization under uncertainty — even when you\'re both the PM and the entire engineering team. It also reinforced that "real-time AI" is as much a performance-engineering problem as a modeling one — caching and throttling mattered as much as the models themselves.',
  },
  {
    id: 'interviewtool',
    number: '05',
    name: 'InterviewLab — Personalized AI Mock Interviewer & Gap Trainer',
    tagline: 'A personalized AI coach that researches the company, finds your gaps, and drills you until you’re ready',
    highlight: 'Full 7-step AI pipeline, live end-to-end',
    status: 'Seeding Users',
    role: 'Product & Engineering',
    tags: [
      'Next.js', 'Postgres & Prisma', 'OpenAI/Anthropic/Gemini Routing',
      'Google Calendar OAuth', 'Stripe Billing', 'Roadmapping',
    ],
    problem:
      'Interview prep is scattered — company research in one tab, the job posting in another, practice questions somewhere else — with nothing that adapts a plan to your actual gaps or your actual calendar.',
    idea:
      'One tool that does what a great mentor would: take a company, a role, an interview date, and a resume, then research the company, analyze the posting, find the gaps, build a day-by-day study plan around your real calendar, and drill you with mock interviews that adapt as you go.',
    approach:
      'Owned this as a full product — architecture, data model, and a 7-step AI pipeline (research, job analysis, resume gap analysis, study-plan generation, calendar-aware scheduling, question generation, scored mock interviews) — the same way I\'d scope a real platform. Wrote the roadmap and backlog myself and prioritized what shipped first.',
    built:
      'A Next.js app on Postgres/Prisma with a multi-provider LLM abstraction (OpenAI, Anthropic, Gemini) that routes each pipeline step to the right model tier and fails over automatically if a provider is down. A deterministic scheduler fits study tasks into real free calendar slots via Google Calendar OAuth, with a replanner that reschedules automatically when a task is missed. Every AI output — company briefings, gap reports, study plans, questions, mock-interview feedback — is schema-validated, not freeform text.',
    challenge:
      'Making an AI pipeline reliable enough to trust with something as high-stakes as interview prep — which meant every step had to output structured, validated data instead of prose, and the model layer needed automatic failover instead of a single point of failure.',
    impact:
      'The core product is built and running end to end — the full 7-step pipeline, calendar-aware scheduling, and scored mock interviews are all live. Now in a growth phase: hardening production auth and storage, and marketing to bring in its first real users.',
    learned:
      'Running my own roadmap and backlog for a personal project made it obvious how much of product management is prioritization under uncertainty — even when you\'re both the PM and the entire engineering team. It also confirmed something I already believed: an AI feature people can actually trust needs structured, validated outputs, not just a good demo.',
  },
  {
    id: 'aichatbot',
    number: '06',
    name: 'BookBot — Automated Voice Agent for Appointment Scheduling',
    tagline: 'Teaching a voicebot to answer the phone and actually book the appointment',
    highlight: 'In active development · targeting Q3 2027',
    status: 'In Progress',
    role: 'Product & Engineering',
    tags: ['OpenAI', 'LangChain', 'Pinecone', 'Speech-to-Text', 'Node.js & React', 'Product Strategy'],
    problem:
      'Small service businesses lose bookings every day to missed calls and slow follow-up — but a full call-center hire isn’t realistic for most of them.',
    idea:
      'A front-desk chatbot/voicebot that can hold a real phone conversation, understand intent, and book directly into a calendar — not just a scripted IVR tree.',
    approach:
      'Prototyping with OpenAI and LangChain for the conversation layer, Pinecone for retrieval, and speech-to-text for the voice interface, wired directly into Google Calendar for booking.',
    built:
      'An in-progress voicebot pipeline: OpenAI + LangChain + Pinecone for understanding and memory, STT for the phone interface, and a Node.js/React service layer connecting it to Google Calendar.',
    challenge:
      'Getting a conversational model to reliably extract structured booking details (date, time, service) from a messy, real phone conversation — without it sounding like a robot reading a script.',
    impact: 'Currently in active development, targeted for broader booking-system integration by Q3 2027.',
    learned:
      'The interesting problems in applied AI right now aren’t "can the model talk" — it’s reliably turning a freeform conversation into structured, trustworthy data downstream.',
  },
];

// Smaller builds — shown with a lighter touch than the full case studies.
export const experiments = [
  {
    id: 'portfolio',
    title: 'This Portfolio',
    stack: 'React, Tailwind CSS, shadcn/ui, Framer Motion, Vercel',
    note: "The site you're on right now — built and designed as a product, not just a resume page, down to the component library.",
    githubLink: 'https://github.com/shubh186/PortfolioWebsite',
  },
  {
    id: 'chess',
    title: 'Chess AI',
    stack: 'Python',
    note: 'Built a chess engine from first principles — Minimax with Alpha-Beta pruning — because I wanted to understand the algorithm, not just call a library and move on.',
    githubLink: 'https://github.com/shubh186/Chess-AI',
  },
  {
    id: 'neuralnet',
    title: 'Neural Network from Scratch',
    stack: 'Python, NumPy',
    note: 'Trained a feedforward neural network on MNIST using nothing but NumPy — no TensorFlow, no PyTorch — to understand backpropagation at the matrix level instead of trusting a framework to handle it for me.',
    githubLink: 'https://github.com/shubh186/Artificial-Neural-Network',
  },
  {
    id: 'bloombuddy',
    title: 'Bloom Buddy',
    stack: 'Flutter, Dart',
    note: 'A plant-care app built to fix a habit people keep abandoning — personalized care reminders and growth tracking so your plants outlive your good intentions.',
    githubLink: 'https://github.com/shubh186/BloomBuddy',
  },
  {
    id: 'nonprofit',
    title: 'Product Manager / Software Developer',
    org: 'Shrimad Rajchandra Mission Dharampur',
    stack: 'Zoho, HubSpot, Python, JavaScript, Product',
    note: 'Ongoing volunteer work for a non-profit — wearing both the product manager and software developer hats to build and manage the systems (Zoho, HubSpot) that run community programs end to end.',
    githubLink: '#',
  },
];

export const contactCopy = {
  eyebrow: '05 — Get in touch',
  heading: "Let's build something.",
  sub:
    "New product, a team that needs someone to bridge the gap between the roadmap and the codebase, or just a good conversation about where AI is actually useful — I'd like to hear about it.",
};
